/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useCallback, useEffect } from 'react';
import type { ComponentType, FunctionComponent } from 'react';
import type { FieldValues, FormState, UseFormRegisterReturn } from 'react-hook-form';
import { Controller, useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';

import { zodResolver } from '@hookform/resolvers/zod';
import type { ZodType } from 'zod';

import { useMemoizedFormState } from '../../hooks/use-form-state';
import { useImmediate } from '../../hooks/use-immediate';
import { createClassName } from '../../utils/class-names';
import { slugify } from '../../utils/strings';
import { Field } from '../field/field';



export enum DisplayType {
    COMPONENT = 'component',
    FIELD = 'field',
}

export interface IOnFieldChangeParameters {
    name: string;
    values: Record<string, any>;
    setValue: (name: string, value: any) => void;
}

export interface IBase {
    name: string;
    props?: Record<string, any>;
}

export interface IComponent extends IBase {
    type: DisplayType.COMPONENT;
    component: ComponentType<any> | string;
}

export interface IField extends IBase {
    label?: string | JSX.Element;
    className?: string;
    component: ComponentType<UseFormRegisterReturn | any> | string;
    type: DisplayType.FIELD;
    displayValidation?: boolean;
    variant?: string;
    options?: { valueAsNumber?: boolean };
}

export type ChangeEvent<OutputType extends FieldValues = any> = {
    formState: FormState<OutputType>;
    values: Partial<OutputType>;
};

export interface IFormBuilderProps {
    className?: string;
    currStep?: any;
    defaultValues?: Record<string, any>;
    formData?: Record<string, any>;
    schema?: ZodType<unknown>;
    components: Readonly<Array<IField | IComponent | undefined | null>>;
    onChange?: (changeEvent: ChangeEvent) => void;
    onFieldChange?: (params: IOnFieldChangeParameters) => void;
    onSubmit?: (formData: any) => void;
    resetForm?: (input: Record<string, unknown>) => Record<string, unknown>;
    errors?: Array<{ type: 'field' | 'form'; fieldName?: string; messages?: string[] }>;
}

export interface IErrorProps {
    hasError?: boolean;
    hasSuccess?: boolean;
    message?: string;
}

export const FormBuilder: FunctionComponent<IFormBuilderProps> = ({
    className,
    schema,
    components,
    defaultValues,
    formData = {},
    onChange,
    onFieldChange,
    onSubmit,
    resetForm,
    errors,
}) => {
    const { register, control, handleSubmit, formState, watch, getValues, setValue, setError } =
        useForm({
            shouldUnregister: true,
            mode: 'all',
            reValidateMode: 'onSubmit',
            resolver: schema ? zodResolver(schema) : undefined,
            defaultValues,
            // Bad typings coming from react-hook-form so we'll just cast here.
            values: resetForm
                ? // eslint-disable-next-line @typescript-eslint/ban-types
                  (resetForm(formData) as Record<string, {} | undefined>)
                : formData,
        });

    const intl = useIntl();

    const [memoizedFormState] = useMemoizedFormState(formState);

    const onStateOrValueChange = useImmediate(
        useCallback(() => {
            const values = getValues();
            onChange?.({ formState: memoizedFormState, values });
        }, [getValues, memoizedFormState, onChange]),
    );

    useEffect(() => {
        const subscription = watch((_, { name }) => {
            const values = getValues();
            onStateOrValueChange();
            onFieldChange?.({
                name: name ?? '',
                values,
                setValue: (name: string, value: any) => {
                    setValue(name, value, { shouldValidate: true, shouldDirty: true });
                },
            });
        });
        return () => subscription.unsubscribe();
    }, [watch, getValues, onFieldChange, onStateOrValueChange, setValue]);

    useEffect(() => {
        onStateOrValueChange();
    }, [onStateOrValueChange]);

    useEffect(() => {
        if (!errors) {
            return;
        }

        errors?.forEach((item) => {
            const errorItemKey = item.fieldName ? item.fieldName : 'root.serverError';

            setError(errorItemKey, { message: item.messages?.[0] });
        });
    }, [errors, setError]);

    return (
        <>
            <form
                onSubmit={onSubmit ? handleSubmit(onSubmit) : undefined}
                className={createClassName([className])}
            >
                {components
                    .filter((value): value is IField | IComponent => !!value)
                    .map((component) => {
                        const FieldComponent = component.component;

                    if (component.type === DisplayType.COMPONENT) {
                        return <FieldComponent key={component.name} {...component.props} />;
                    }

                    const error = formState.errors?.[component.name]?.message;
                    const touched = formState.touchedFields?.[component.name];
                    const isController = FieldComponent === Controller;
                    const hideValidation = component.displayValidation === false;
                    const variant = component.variant;
                    const controlProps = isController
                        ? {
                              control,
                              name: component.name,
                          }
                        : register(component.name, component.options);

                    const errorMessage = typeof error === 'string' ? error : undefined;
                    const errorProps: IErrorProps = {
                        hasError: hideValidation ? false : !!error,
                        hasSuccess: hideValidation ? false : !error && !!touched,
                        message: hideValidation
                            ? undefined
                            : errorMessage
                            ? intl.formatMessage({
                                  id: `form-builder.${slugify(errorMessage)}.error`,
                                  defaultMessage: errorMessage,
                              })
                            : errorMessage,
                    };

                    return (
                        <Field
                            key={component.name}
                            className={component.className}
                            label={component.label}
                            info={
                                component.props?.['info'] ? (
                                    <FormattedMessage
                                        id={component.props?.['info']}
                                        defaultMessage={component.props?.['info']}
                                    />
                                ) : undefined
                            }
                            htmlFor={component.name}
                            variant={variant}
                            {...errorProps}
                        >
                            <FieldComponent
                                {...component.props}
                                {...controlProps}
                                {...errorProps}
                            />
                        </Field>
                    );
                })}
        </form>
    </>
    );
};
